/**
 * remark plugin: detects `{+0.8 pts/100}` / `{-1.8 QSQ}` patterns in
 * markdown text nodes and emits a styled span node.
 *
 * Pattern: `{<sign><number> <unit>}` where sign is `+` or `-`, number is
 * a decimal, unit is any non-`}` characters. The unit is preserved verbatim
 * in the rendered chip (so percentages, abbreviations, multi-word units
 * all work).
 *
 * The emitted node uses `data.hName: 'span'` so it survives the
 * mdast→hast conversion without needing `rehype-raw` (which would open
 * the door to inline HTML / XSS). ArticleSection renders these as
 * `<DeltaChip>` via a `components.span` override that inspects className.
 *
 * Examples:
 *   "edge of {+0.8 pts/100}"      → "edge of <span class='delta-chip delta-positive'>+0.8 pts/100</span>"
 *   "QSQ {-1.8} on similar shots" → "QSQ <span class='delta-chip delta-negative'>-1.8</span> on similar shots"
 */

import type { Node, Parent } from "unist";

interface TextNode extends Node {
  type: "text";
  value: string;
}

const DELTA_PATTERN = /\{([+-])(\d+(?:\.\d+)?)\s*([^}]*)\}/g;

const remarkDeltaChip = () => {
  return (tree: Node) => {
    const transform = (
      node: Node,
      parent: Parent | null,
      index: number,
    ): number | undefined => {
      // Recurse into children first (depth-first) so we don't process
      // children of replaced nodes.
      const asParent = node as Parent;
      if (Array.isArray(asParent.children)) {
        let i = 0;
        while (i < asParent.children.length) {
          const consumed = transform(asParent.children[i], asParent, i);
          i += consumed ?? 1;
        }
      }

      if (node.type !== "text" || !parent) return;
      const text = (node as TextNode).value;
      DELTA_PATTERN.lastIndex = 0;
      const matches: RegExpMatchArray[] = [];
      let m: RegExpExecArray | null;
      while ((m = DELTA_PATTERN.exec(text)) !== null) {
        matches.push(m);
      }
      if (matches.length === 0) return;

      const newNodes: Node[] = [];
      let lastEnd = 0;
      for (const match of matches) {
        const start = match.index ?? 0;
        if (start > lastEnd) {
          newNodes.push({
            type: "text",
            value: text.slice(lastEnd, start),
          } as TextNode);
        }
        const sign = match[1];
        const num = match[2];
        const unit = (match[3] || "").trim();
        const label = unit ? `${sign}${num} ${unit}` : `${sign}${num}`;
        newNodes.push({
          type: "deltaChip",
          data: {
            hName: "span",
            hProperties: {
              className: [
                "delta-chip",
                sign === "+" ? "delta-positive" : "delta-negative",
              ],
              "data-sign": sign,
              "data-value": num,
              "data-unit": unit,
            },
            hChildren: [{ type: "text", value: label }],
          },
        } as Node);
        lastEnd = start + match[0].length;
      }
      if (lastEnd < text.length) {
        newNodes.push({ type: "text", value: text.slice(lastEnd) } as TextNode);
      }

      parent.children.splice(index, 1, ...newNodes);
      return newNodes.length;
    };

    transform(tree, null, 0);
  };
};

export default remarkDeltaChip;
